import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { getLastOrderSegment, getOrdersList } from "./../redux/actions/action-creator";
import OrderTabs from "../components/orders/OrderTabs";

import Preloader from "../helper/Preloader";
import Header from "../components/Header";
import Breadcrumb from "../components/Breadcrumb";
import BottomFooter from "../components/BottomFooter";
import Orders from "../components/orders/Orders";
import ScrollToTop from "react-scroll-to-top";
import ColorInit from "../helper/ColorInit";
import MyServices from "../components/MyServices";
import { getSegmentId, getSegmentItems } from "../utils/segmentFlow";
import "./OrdersPage.css";

const ORDERS_PER_PAGE = 10;
const emptyPagination = () => ({
  currentPage: 1,
  hasMore: false,
  perPage: ORDERS_PER_PAGE,
  total: 0,
  totalPages: 1,
});

const isOfferService = (service = {}) => {
  const serviceName = String(service?.name || service?.title || service?.service_name || "")
    .trim()
    .toLowerCase();

  return serviceName === "offer" || serviceName === "offers";
};

const normalizeBoolean = (value) => {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;

  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    if (normalized === "true" || normalized === "yes") return true;
    if (normalized === "false" || normalized === "no") return false;
  }

  return Boolean(value);
};

const getOrdersFromResponse = (response = {}) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;

  const possibleLists = [
    data?.orders,
    data?.orders_list,
    data?.order_list,
    data?.order,
    data?.items,
    data?.data,
    data?.list,
    data?.bookings,
    response?.orders,
    response?.orders_list,
    response?.order_list,
    response?.items,
  ];

  return possibleLists.find((list) => Array.isArray(list)) || [];
};

const getPaginationFromResponse = (response = {}, page = 1, fallbackLength = 0) => {
  const data = response?.data || {};
  const meta = response?.meta || data?.meta || data?.pagination || response?.pagination || {};
  const currentPage = Number(
    meta?.current_page ||
      meta?.currentPage ||
      data?.current_page ||
      data?.currentPage ||
      response?.current_page ||
      response?.currentPage ||
      page
  ) || page;
  const perPage = Number(
    meta?.per_page ||
      meta?.perPage ||
      data?.per_page ||
      data?.perPage ||
      response?.per_page ||
      response?.perPage ||
      ORDERS_PER_PAGE
  ) || ORDERS_PER_PAGE;
  const total = Number(
    meta?.total ||
      meta?.total_count ||
      meta?.totalCount ||
      data?.total ||
      data?.total_count ||
      response?.total ||
      0
  ) || 0;
  const totalPages = Number(
    meta?.last_page ||
      meta?.lastPage ||
      meta?.total_pages ||
      meta?.totalPages ||
      data?.last_page ||
      data?.total_pages ||
      response?.last_page ||
      response?.total_pages ||
      0
  ) || (total ? Math.ceil(total / perPage) : 0);
  const explicitHasMore =
    meta?.has_more ??
    meta?.hasMore ??
    data?.has_more ??
    data?.hasMore ??
    response?.has_more ??
    response?.hasMore;
  const hasNextPage =
    Boolean(meta?.next_page_url || data?.next_page_url || response?.next_page_url) ||
    Boolean(meta?.next_page || data?.next_page || response?.next_page);

  return {
    currentPage,
    perPage,
    total,
    totalPages: totalPages || currentPage,
    hasMore:
      explicitHasMore !== undefined
        ? normalizeBoolean(explicitHasMore)
        : hasNextPage || (totalPages ? currentPage < totalPages : fallbackLength >= perPage),
  };
};

const mergeOrders = (previousOrders, nextOrders) => {
  const seen = new Set();

  return [...previousOrders, ...nextOrders].filter((order, index) => {
    const key = order?.order_id || order?.id || order?.order_no || `index-${index}`;

    if (seen.has(String(key))) return false;
    seen.add(String(key));
    return true;
  });
};

const OrdersPage = () => {
  const dispatch = useDispatch();
  const [services, setServices] = useState(null);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesFailed, setServicesFailed] = useState(false);
  const [servicesAttempt, setServicesAttempt] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState(2);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersLoadingMore, setOrdersLoadingMore] = useState(false);
  const [ordersPagination, setOrdersPagination] = useState(emptyPagination);
  const [loadedOrdersKey, setLoadedOrdersKey] = useState(null);
  const servicesRequestRef = useRef(null);
  const ordersRequestRef = useRef(0);
  const loadMoreRequestRef = useRef(false);
  const ordersKey = `${selectedService?.segment_id}:${activeTab}`;

  useEffect(() => {
    let active = true;
    setServicesLoading(true);
    setServicesFailed(false);
    setServices(null);
    setSelectedService(null);

    // Reuse the mount request when StrictMode replays the effect.
    if (!servicesRequestRef.current) {
      servicesRequestRef.current = dispatch(getLastOrderSegment());
    }

    servicesRequestRef.current
      .then((response) => {
        if (!active) return;

        const serviceItems = getSegmentItems(response)
          .filter((service) => service && getSegmentId(service) && !isOfferService(service))
          .map((service) => ({
            ...service,
            segment_id: getSegmentId(service),
            name: service.name || service.title || service.service_name || "Service",
            image: service.segment_icon || service.image || "",
          }));

        setServices({ cell_title_text: "My Services", cell_contents: serviceItems });
        setSelectedService(serviceItems[0] || null);
      })
      .catch(() => {
        if (active) setServicesFailed(true);
      })
      .finally(() => {
        if (active) setServicesLoading(false);
      });

    return () => { active = false; };
  }, [dispatch, servicesAttempt]);

  const retryServices = () => {
    servicesRequestRef.current = null;
    setServicesAttempt((attempt) => attempt + 1);
  };

  const loadOrders = useCallback(async (page = 1, append = false) => {
    if (!selectedService) return;

    const requestId = ++ordersRequestRef.current;
    const requestKey = `${selectedService.segment_id}:${activeTab}`;

    if (append) {
      setOrdersLoadingMore(true);
    } else {
      setOrdersLoading(true);
      setOrdersLoadingMore(false);
      setOrders([]);
      setOrdersPagination(emptyPagination());
      setLoadedOrdersKey(null);
    }

    const formData = new FormData();
    formData.append("segment_id", selectedService.segment_id);
    formData.append("type", activeTab);
    formData.append("page", page);
    formData.append("per_page", ORDERS_PER_PAGE);

    try {
      const response = await dispatch(getOrdersList(formData));
      if (requestId !== ordersRequestRef.current) return;

      const nextOrders = getOrdersFromResponse(response);
      const pagination = getPaginationFromResponse(response, page, nextOrders.length);

      setOrders((previousOrders) =>
        append ? mergeOrders(previousOrders, nextOrders) : nextOrders
      );
      setOrdersPagination((previousPagination) => ({
        ...previousPagination,
        ...pagination,
      }));
      setLoadedOrdersKey(requestKey);
    } catch (error) {
      if (requestId !== ordersRequestRef.current) return;
      if (!append) {
        setOrders([]);
        setOrdersPagination(emptyPagination());
        setLoadedOrdersKey(requestKey);
      }
    } finally {
      if (requestId === ordersRequestRef.current) {
        setOrdersLoading(false);
        setOrdersLoadingMore(false);
      }
    }
  }, [activeTab, dispatch, selectedService]);

  const loadMoreOrders = useCallback(async () => {
    if (
      ordersLoading ||
      ordersLoadingMore ||
      !ordersPagination.hasMore ||
      loadMoreRequestRef.current
    ) {
      return;
    }

    loadMoreRequestRef.current = true;

    try {
      await loadOrders(ordersPagination.currentPage + 1, true);
    } finally {
      loadMoreRequestRef.current = false;
    }
  }, [
    loadOrders,
    ordersLoading,
    ordersLoadingMore,
    ordersPagination.currentPage,
    ordersPagination.hasMore,
  ]);

  useEffect(() => {
    if (servicesLoading || !selectedService) return;

    loadOrders(1, false);
    return () => {
      ordersRequestRef.current += 1;
      loadMoreRequestRef.current = false;
    };
  }, [loadOrders, servicesLoading, selectedService]);

  return (
    <div className="bg-white orders-page-shell">
      {/* Preloader */}
      <Preloader />

      {/* ScrollToTop */}
      <ScrollToTop smooth color="#299E60" />

      {/* ColorInit */}
      <ColorInit color={false} />

      {/* Header */}
      <Header category={true} />

      {/* Breadcrumb */}
      <Breadcrumb title={"My Orders"} />


      <main className="orders-page-content" aria-busy={servicesLoading}>
        {servicesLoading ? (
          <>
            <p className="orders-services-loading" role="status">Loading your services...</p>
            <MyServices loading />
          </>
        ) : selectedService ? (
          <>
            <MyServices
              services={services}
              selectedService={selectedService}
              setSelectedService={setSelectedService}
            />
            <OrderTabs activeTab={activeTab} setActiveTab={setActiveTab} />
            <Orders
              orders={orders}
              loading={ordersLoading || loadedOrdersKey !== ordersKey}
              onOrdersRefresh={() => loadOrders(1, false)}
              onLoadMoreOrders={loadMoreOrders}
              loadingMore={ordersLoadingMore}
              hasMoreOrders={ordersPagination.hasMore}
              segmentId={selectedService.segment_id}
            />
          </>
        ) : (
          <section className="orders-services-state" aria-labelledby="orders-services-empty-title">
            <div className="orders-services-state__card">
              <span className="orders-services-state__icon" aria-hidden="true">
                <i className="ph ph-receipt" />
              </span>
              <p className="orders-services-state__eyebrow">MY ORDERS</p>
              <h2 id="orders-services-empty-title">No services found</h2>
              <p className="orders-services-state__description">
                {servicesFailed
                  ? "We couldn't load your services right now. Please try again."
                  : "Your ordered services will appear here once you place your first order."}
              </p>
              <div className="orders-services-state__actions">
                <Link to="/home" className="orders-services-state__browse">Browse services</Link>
                <button type="button" className="orders-services-state__retry" onClick={retryServices}>
                  <i className="ph ph-arrow-clockwise" aria-hidden="true" />
                  Try again
                </button>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* BottomFooter */}
      <BottomFooter />
    </div>
  );
};

export default OrdersPage;
